import { IconAward, IconCalendarDue, IconCheck, IconCoins, IconHourglass, IconSparkles } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { api, ApiError, type LoyaltyTier, type LoyaltyView } from "../api";
import { Card, ErrorNote, SectionTitle, Spinner } from "../components/ui";
import { formatDate, formatMoney, formatNumber } from "../format";
import { useSession } from "../session";

/** Getaways Club (docs/16 §2.9, decision 15): balance, tier and progress, how it works, the three tiers and their benefits. */
export function ClubPage() {
  const { me, brand, lang, t } = useSession();
  const [view, setView] = useState<LoyaltyView | null | undefined>(undefined);
  const [error, setError] = useState<{ message: string; signIn?: boolean } | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    setError(null);
    api
      .loyalty(lang)
      .then((res) => setView(res.loyalty))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.code === "sign_in_required") setError({ message: t("linkSessionNote"), signIn: true });
        else setError({ message: t("somethingWrong") });
      });
  }, [tick, t, lang]);

  if (!me || !brand) return null;
  const programme = view?.programme ?? brand.loyaltyProgramme;

  return (
    <main className="page page-club">
      <h1>{programme}</h1>
      {error ? (
        <>
          <ErrorNote onRetry={error.signIn ? undefined : () => setTick((n) => n + 1)}>{error.message}</ErrorNote>
          {error.signIn && (
            <a href="/signin" className="button button-primary">
              {t("signIn")}
            </a>
          )}
        </>
      ) : view === undefined ? (
        <Spinner />
      ) : view === null ? (
        <Card className="loyalty-card loyalty-join club-hero">
          <IconSparkles size={28} stroke={1.5} />
          <h2>{t("clubEmptyTitle", { programme })}</h2>
          <p className="small">{t("clubEmptyBody")}</p>
        </Card>
      ) : (
        <ClubHero view={view} />
      )}

      <SectionTitle>{t("howItWorks")}</SectionTitle>
      <Card className="how-card">
        <ul className="how-list">
          <li>
            <IconCoins size={20} stroke={1.5} />
            <span>{t("howEarn")}</span>
          </li>
          <li>
            <IconAward size={20} stroke={1.5} />
            <span>{t("howRedeem")}</span>
          </li>
          <li>
            <IconHourglass size={20} stroke={1.5} />
            <span>{t("howExpire")}</span>
          </li>
        </ul>
        <p className="muted small">{t("clubBrandsNote")}</p>
      </Card>

      {view && view.tiers.length > 0 && (
        <>
          <SectionTitle>{t("tiersTitle")}</SectionTitle>
          <div className="stack">
            {view.tiers.map((tier) => (
              <TierCard key={tier.code} tier={tier} current={view.tier?.code === tier.code} />
            ))}
          </div>
        </>
      )}
    </main>
  );
}

function ClubHero({ view }: { view: LoyaltyView }) {
  const { lang, t } = useSession();
  const pct = view.next ? Math.round(Math.min(1, Math.max(0, view.next.progress)) * 100) : 100;
  const ring = { background: `conic-gradient(#fff ${pct}%, rgba(255, 255, 255, 0.25) 0)` };
  return (
    <Card className="loyalty-card club-hero">
      <div className="club-hero-row">
        <div className="club-hero-text">
          <span className="eyebrow">{t("yourTier")}</span>
          <h2 className="club-tier">{view.tier?.name ?? view.tiers[0]?.name ?? "—"}</h2>
          <p className="small">{view.next ? t("progressToNext", { pct, tier: view.next.name }) : t("topTier")}</p>
        </div>
        <div className="ring" style={ring} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={view.next ? t("progressToNext", { pct, tier: view.next.name }) : t("topTier")}>
          <span className="ring-inner">{pct}%</span>
        </div>
      </div>
      <ul className="balances">
        {view.balances.length === 0 && (
          <li>
            <span className="loyalty-points">0</span>
            <span className="small"> {t("points")}</span>
          </li>
        )}
        {view.balances.map((b) => (
          <li key={b.currency}>
            <span className="loyalty-points">{formatNumber(b.points, lang)}</span>
            <span className="small"> {t("points")} · {t("worth", { value: formatMoney(b.valueMinor, b.currency, lang) })}</span>
          </li>
        ))}
      </ul>
      {view.expiresAt && (
        <p className="small club-expiry">
          <IconCalendarDue size={16} /> {t("pointsExpire", { date: formatDate(view.expiresAt.slice(0, 10), lang) })}
        </p>
      )}
    </Card>
  );
}

function TierCard({ tier, current }: { tier: LoyaltyTier; current: boolean }) {
  const { lang, t } = useSession();
  return (
    <Card className={`tier-card tier-${tier.code} ${current ? "tier-current" : ""}`}>
      <div className="tier-head">
        <span className="list-icon">
          <IconAward size={20} stroke={1.5} />
        </span>
        <div className="list-text">
          <strong>{tier.name}</strong>
          <span className="muted small">{tier.threshold > 0 ? t("fromPoints", { points: formatNumber(tier.threshold, lang) }) : t("fromFirstTrip")}</span>
        </div>
        {current && <span className="chip chip-current">{t("current")}</span>}
      </div>
      <ul className="benefits">
        {tier.benefits.map((b) => (
          <li key={b}>
            <IconCheck size={16} /> <span>{b}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
